/**
 * Lesson MCQ Schema
 * A content type that combines lesson content (Page) with MCQ questions in a split layout
 */

import type { ContentConfig, LayoutConfig, ScoringConfig } from '@ai-content-hub/content-core';
import type { PageElementData, MCQSetData } from '@ai-content-hub/content-elements';

/**
 * Split ratio options for the layout
 */
export type SplitRatio = '50-50' | '60-40' | '40-60' | '70-30' | '30-70';

/**
 * Layout configuration for Lesson MCQ
 */
export interface LessonMCQLayoutConfig extends LayoutConfig {
    /** Split ratio between left (lesson) and right (questions) panels */
    splitRatio?: SplitRatio;
    /** Whether to stack panels vertically on mobile */
    responsive?: boolean;
    /** Minimum height for panels */
    minHeight?: string;
    /** How the MCQ set lists questions: single page (`all`) or multi-page tabs (`tabs`). Legacy `step` = `tabs` in the player. */
    displayMode?: 'all' | 'step' | 'tabs';
}

/**
 * Scoring configuration for Lesson MCQ
 */
export interface LessonMCQScoringConfig extends ScoringConfig {
    /** Points per question (default: 1) */
    pointsPerQuestion?: number;
    /** Passing threshold (0-1) */
    passingThreshold?: number;
}

/**
 * Lesson MCQ Config
 */
export interface LessonMCQConfig extends Omit<ContentConfig, 'layout' | 'scoring'> {
    layout?: LessonMCQLayoutConfig;
    scoring?: LessonMCQScoringConfig;
}

/**
 * Lesson MCQ Data
 */
export interface LessonMCQData {
    /** Optional title for the lesson */
    title?: string;
    /** Optional description */
    description?: string;
    /** Left side - lesson content using Page element */
    lessonContent: PageElementData;
    /** Right side - questions using MCQ Set */
    questionSet: MCQSetData;
}

/**
 * Lesson MCQ Content - complete content item
 */
export interface LessonMCQContent {
    id: string;
    type: 'lesson-mcq';
    version: string;
    data: LessonMCQData;
    config?: LessonMCQConfig;
}

/**
 * Default i18n translations
 */
export const defaultLessonMCQI18n = {
    en: {
        lessonTab: 'Lesson',
        questionsTab: 'Questions',
        submit: 'Submit Answers',
        retry: 'Try Again',
        score: 'Score',
        passed: 'Passed!',
        failed: 'Keep practicing',
        descriptionOptional: 'Description (Optional)',
        enterTitlePlaceholder: 'Enter title',
        enterDescriptionPlaceholder: 'Enter description or instructions for learners',
        lessonContentHeading: 'Lesson Content',
        questionsHeading: 'Questions',
        title: 'Title',
        configuration: 'Configuration',
        noContent: 'No content available',
    },
    vi: {
        lessonTab: 'Bài học',
        questionsTab: 'Câu hỏi',
        submit: 'Gửi câu trả lời',
        retry: 'Thử lại',
        score: 'Điểm',
        passed: 'Đã vượt qua!',
        failed: 'Tiếp tục luyện tập',
        descriptionOptional: 'Mô tả (tuỳ chọn)',
        enterTitlePlaceholder: 'Nhập tiêu đề',
        enterDescriptionPlaceholder: 'Nhập mô tả hoặc hướng dẫn cho học viên',
        lessonContentHeading: 'Nội dung bài học',
        questionsHeading: 'Câu hỏi',
        title: 'Tiêu đề',
        configuration: 'Cấu hình',
        noContent: 'Không có nội dung',
    },
};

/**
 * Default config
 */
export const defaultLessonMCQConfig: LessonMCQConfig = {
    layout: {
        type: 'auto',
        splitRatio: '50-50',
        responsive: true,
        minHeight: '500px',
        displayMode: 'all',
    },
    behavior: {
        allowRetry: true,
        showCorrectAnswer: true,
        showExplanation: true,
    },
    scoring: {
        pointsPerQuestion: 1,
        partialCreditEnabled: false,
        passingThreshold: 0.7,
    },
    i18n: {
        defaultLanguage: 'en',
        languages: defaultLessonMCQI18n,
    },
};

/**
 * Default data
 */
export const defaultLessonMCQData: LessonMCQData = {
    // EAA-2082: a new lesson starts with EMPTY titles so the editor shows only the
    // placeholder. "Lesson"/"Questions" were picked up as the content title (the
    // question-bank page and the EAA-2031 auto-fill both read questionSet.title).
    title: '',
    description: '',
    lessonContent: {
        title: '',
        description: '',
        sections: [],
    },
    questionSet: {
        title: '',
        description: undefined,
        items: [],
    },
};

/**
 * Schema definition
 */
export const lessonMCQSchema = {
    type: 'lesson-mcq',
    title: 'Lesson MCQ',
    description:
        'A split-view content type with lesson content on the left and MCQ questions on the right',
    version: '1.0.0',
    category: 'assessment',
    icon: 'BookOpen',
    tags: ['lesson', 'mcq', 'assessment', 'split-view'],
    defaultData: defaultLessonMCQData,
    defaultConfig: defaultLessonMCQConfig,
};

/**
 * Get CSS grid columns based on split ratio
 */
export function getSplitGridCols(ratio: SplitRatio): string {
    switch (ratio) {
        case '50-50':
            return 'grid-cols-1 lg:grid-cols-2';
        case '60-40':
            return 'grid-cols-1 lg:grid-cols-[60%_40%]';
        case '40-60':
            return 'grid-cols-1 lg:grid-cols-[40%_60%]';
        case '70-30':
            return 'grid-cols-1 lg:grid-cols-[70%_30%]';
        case '30-70':
            return 'grid-cols-1 lg:grid-cols-[30%_70%]';
        default:
            return 'grid-cols-1 lg:grid-cols-[60%_40%]';
    }
}
